import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import type { ConversationPartApiResponse } from "@f2e/ui/chat-v2"
import { Button } from "@f2e/ui/components/base"
import { useStore } from "@/store"
import { hasPermission } from "@/lib/utils"
import { Permission } from "@/types"
import { useReplyAttachmentActionApi } from "@/hooks/api/histories"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

export interface PlatformReplyAttachmentProps {
  part: ConversationPartApiResponse
  workspace?: { myPermissions?: Set<string> }
  canResend: boolean
  threadKey?: string
  onChanged: () => void
  resolveUrl?: (url: string) => string
}

export const PlatformReplyAttachment = ({
  part,
  workspace,
  canResend,
  threadKey,
  onChanged,
  resolveUrl,
}: PlatformReplyAttachmentProps) => {
  const { t } = useTranslation()
  const organization = useStore((state) => state.currentOrganization)
  const canEdit = Boolean(
    organization &&
    hasPermission(
      new Set([
        ...organization.myPermissions,
        ...(workspace?.myPermissions ?? []),
      ]),
      Permission.WsUpdateResources
    )
  )
  const reply = part.metadata.platform_reply
  const attachment = reply?.attachment
  const { fetchFn: revoke } = useReplyAttachmentActionApi(
    reply?.id ?? 0,
    "revoke"
  )
  const { fetchFn: resend } = useReplyAttachmentActionApi(
    reply?.id ?? 0,
    "resend"
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const resendKey = useRef<string | null>(null)
  const act = async (action: "revoke" | "resend") => {
    if (busy) return
    setBusy(true)
    setError(undefined)
    try {
      if (action === "resend") {
        resendKey.current ??= crypto.randomUUID()
        await resend({
          jsonBody: {
            idempotency_key: resendKey.current,
            ...(threadKey ? { thread_key: threadKey } : {}),
          },
        })
        resendKey.current = null
      } else await revoke({ jsonBody: {} })
      onChanged()
    } catch (err) {
      setError(
        getUserFacingApiErrorMessage(
          err,
          t,
          t("history.replyAttachment.actionFailed")
        )
      )
    } finally {
      setBusy(false)
    }
  }
  if (!attachment) return null
  const share = attachment.share
  return (
    <div
      className="mt-2 rounded-md border p-2 text-xs font-normal"
      aria-live="polite"
    >
      {attachment.download_url ? (
        <a
          className="underline"
          href={
            resolveUrl?.(attachment.download_url) ?? attachment.download_url
          }
          target="_blank"
          rel="noreferrer"
        >
          {attachment.name}
        </a>
      ) : (
        attachment.name
      )}
      <span>
        {" "}
        · {attachment.content_type} · {attachment.size.toLocaleString()} B
      </span>
      {attachment.accepted === true && (
        <div>{t("history.replyAttachment.accepted")}</div>
      )}
      {share && (
        <>
          <div>
            {t(`history.replyAttachment.${share.status}`)}
            {share.expires_at && (
              <>
                {" "}
                ·{" "}
                {t("history.replyAttachment.expires", {
                  date: new Date(share.expires_at).toLocaleString(),
                })}
              </>
            )}
          </div>
        </>
      )}
      {reply?.id != null && (
        <div className="mt-1 flex gap-2">
          {share && (
            <Button
              size="sm"
              variant="outline"
              title={!canEdit ? t("common.noPermission") : undefined}
              disabled={
                !canEdit ||
                busy ||
                !["pending", "active"].includes(share.status)
              }
              onClick={() => void act("revoke")}
            >
              {t("history.replyAttachment.revoke")}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            title={!canEdit ? t("common.noPermission") : undefined}
            disabled={
              !canEdit ||
              busy ||
              !canResend ||
              reply.status === "sending" ||
              share?.status === "pending"
            }
            onClick={() => void act("resend")}
          >
            {t(
              share
                ? "history.replyAttachment.resend"
                : "history.replyAttachment.resendImage"
            )}
          </Button>
        </div>
      )}
      {error && (
        <div role="alert" className="text-destructive">
          {error}
        </div>
      )}
    </div>
  )
}
